import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Collapse, List, ListItemButton, ListItemIcon, ListItemText,
  Chip, Tooltip, CircularProgress, Alert, Snackbar, Divider,
  Autocomplete, Tabs, Tab, FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormControlLabel,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import api from '../../services/api.js';
import { toHebrewNumeral } from '@NP-Webber/crm-kit';
import ExamBlanks from './ExamBlanks.jsx';
import ExamIssuanceWizard from './ExamIssuanceWizard.jsx';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { ckConfirm } from '@NP-Webber/crm-kit/dist/next/index.js';

/**
 * ניקוי HTML — מאפשר רק <b>, <i>, <u> לתצוגה בטוחה עם dangerouslySetInnerHTML.
 */
const safeRenderQuestion = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/<b[^>]*>/gi,      '\x00BO\x00')
    .replace(/<\/b>/gi,          '\x00BC\x00')
    .replace(/<strong[^>]*>/gi, '\x00BO\x00')
    .replace(/<\/strong>/gi,    '\x00BC\x00')
    .replace(/<i[^>]*>/gi,      '\x00IO\x00')
    .replace(/<\/i>/gi,          '\x00IC\x00')
    .replace(/<em[^>]*>/gi,     '\x00IO\x00')
    .replace(/<\/em>/gi,         '\x00IC\x00')
    .replace(/<u[^>]*>/gi,      '\x00UO\x00')
    .replace(/<\/u>/gi,          '\x00UC\x00')
    .replace(/<[^>]+>/gi, '')
    .replace(/\x00BO\x00/g, '<b>')
    .replace(/\x00BC\x00/g, '</b>')
    .replace(/\x00IO\x00/g, '<i>')
    .replace(/\x00IC\x00/g, '</i>')
    .replace(/\x00UO\x00/g, '<u>')
    .replace(/\x00UC\x00/g, '</u>');
};

/**
 * ניקוי innerHTML של contentEditable — מנרמל ושומר רק <b>/<i>/<u>.
 */
const sanitizeEditorHtml = (html) => {
  if (!html) return '';
  return String(html)
    .replace(/<b[^>]*>/gi,      '\x00BO\x00')
    .replace(/<\/b>/gi,          '\x00BC\x00')
    .replace(/<strong[^>]*>/gi, '\x00BO\x00')
    .replace(/<\/strong>/gi,    '\x00BC\x00')
    .replace(/<i[^>]*>/gi,      '\x00IO\x00')
    .replace(/<\/i>/gi,          '\x00IC\x00')
    .replace(/<em[^>]*>/gi,     '\x00IO\x00')
    .replace(/<\/em>/gi,         '\x00IC\x00')
    .replace(/<u[^>]*>/gi,      '\x00UO\x00')
    .replace(/<\/u>/gi,          '\x00UC\x00')
    .replace(/<div>/gi, '\n').replace(/<\/div>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/gi, '')
    .replace(/\x00BO\x00/g, '<b>')
    .replace(/\x00BC\x00/g, '</b>')
    .replace(/\x00IO\x00/g, '<i>')
    .replace(/\x00IC\x00/g, '</i>')
    .replace(/\x00UO\x00/g, '<u>')
    .replace(/\x00UC\x00/g, '</u>')
    .replace(/&nbsp;/g, ' ')
    .trim();
};

const IS_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const MODE_LABEL = IS_GEMARA ? 'ספריית בחינות גמרא' : 'ספריית בחינות הלכה';

const HALACHA_PARTS = ['אורח חיים', 'יורה דעה', 'אבן העזר', 'חושן משפט'];

/* רשימת מסכתות לבחירה (גמרא) */
const TRACTATE_LIST = [
  'ברכות','שבת','עירובין','פסחים','יומא','סוכה','ביצה','ראש השנה','תענית','מגילה',
  'מועד קטן','חגיגה','יבמות','כתובות','נדרים','נזיר','סוטה','גיטין','קידושין',
  'בבא קמא','בבא מציעא','בבא בתרא','סנהדרין','מכות','שבועות','עבודה זרה','הוריות',
  'זבחים','מנחות','חולין','בכורות','ערכין','תמורה','כריתות','מעילה','תמיד','נידה',
];

/* ═══════════════════════ חלק ראשי ═══════════════════════ */
const ExamLibrary = () => {
  const inNextDesign = useCkInside();
  const [activeTab, setActiveTab] = useState(0);
  const [issuanceOpen, setIssuanceOpen] = useState(false);

  const [difficulties, setDifficulties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // תיקיה פתוחה ברמה 1 (difficulty)
  const [openDiff, setOpenDiff] = useState(null);
  // עץ תיקיות של ה-difficulty הנוכחי
  const [tree, setTree] = useState({});
  const [treeLoading, setTreeLoading] = useState(false);

  // תיקיות פתוחות ברמה 2 (chelek/tractate)
  const [openLevel2, setOpenLevel2] = useState(null);

  // שאלות – נפתחות בלחיצה על סימן/פרק
  const [selectedLeaf, setSelectedLeaf] = useState(null); // { chelek, siman } or { tractate, perek }
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  // דיאלוג הוספת רמת קושי
  const [newDiffDialog, setNewDiffDialog] = useState(false);
  const [newDiffName, setNewDiffName] = useState('');

  // דיאלוג הוספת סימן/פרק (רצף)
  const [newLeafDialog, setNewLeafDialog] = useState(false);
  const [newLeafFrom, setNewLeafFrom] = useState('');
  const [newLeafTo, setNewLeafTo] = useState('');
  const [newLeafParent, setNewLeafParent] = useState('');

  // דיאלוג הוספת מסכתא חדשה (גמרא בלבד) — עם רצף פרקים
  const [newTractateDialog, setNewTractateDialog] = useState(false);
  const [newTractateValue, setNewTractateValue] = useState('');
  const [newTractatePerekFrom, setNewTractatePerekFrom] = useState('');
  const [newTractatePerekTo, setNewTractatePerekTo] = useState('');
  // פרקים עם שמות (גמרא) — למסכתא חדשה
  const [newTractatePerakim, setNewTractatePerakim] = useState([{ perek: '', name: '' }]);
  const [newTractateMode, setNewTractateMode] = useState('range'); // 'range' | 'named'

  // דיאלוג הוספת פרק/סימן לרמה 2 קיימת
  const [addPerekDialog, setAddPerekDialog] = useState(false);
  const [addPerekParent, setAddPerekParent] = useState('');
  const [addPerekNum, setAddPerekNum] = useState('');
  const [addPerekName, setAddPerekName] = useState('');

  // ייבוא Excel / Word
  const [importDialog, setImportDialog] = useState(false);
  const [importSourceTab, setImportSourceTab] = useState(0); // 0=Excel, 1=Word
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Word wizard
  const [docxStep, setDocxStep] = useState(0); // 0=upload+meta, 1=preview
  const [docxDifficulty, setDocxDifficulty] = useState('');
  const [docxChelek, setDocxChelek] = useState(HALACHA_PARTS[0]);
  const [docxTractate, setDocxTractate] = useState('');
  const [docxPerek, setDocxPerek] = useState('');
  const [docxPreview, setDocxPreview] = useState(null); // { questions, stats, warnings }
  const [docxQuestions, setDocxQuestions] = useState([]);

  // דיאלוג הוספת/עריכת שאלה
  const [questionDialog, setQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionText, setQuestionText] = useState('');
  const [questionStarred, setQuestionStarred] = useState(false);
  const editorRef = useRef(null);

  // callback ref — נקרא כשה-contentEditable div נכנס ל-DOM (אחרי שה-Dialog נפתח)
  const editorRefCallback = useCallback((node) => {
    editorRef.current = node;
    if (node) {
      node.innerHTML = editingQuestion ? (editingQuestion.questionText || '') : '';
      // מיקום הסמן בסוף
      try {
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(node);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      } catch { /* ignore */ }
    }
  // eslint-disable-next-line
  }, [editingQuestion]);

  /* ─── טעינת רמות קושי ─── */
  const loadDifficulties = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/exam-library/difficulties');
      setDifficulties(data);
    } catch (err) {
      setError('שגיאה בטעינת רמות הקושי');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDifficulties(); }, [loadDifficulties]);

  /* ─── טעינת עץ לרמת קושי ─── */
  const loadTree = useCallback(async (difficulty) => {
    setTreeLoading(true);
    try {
      const { data } = await api.get(`/exam-library/tree/${encodeURIComponent(difficulty)}`);
      setTree(data);
    } catch (err) {
      setError('שגיאה בטעינת מבנה התיקיות');
    } finally {
      setTreeLoading(false);
    }
  }, []);

  /* ─── טעינת שאלות ─── */
  const loadQuestions = useCallback(async (difficulty, params) => {
    setQuestionsLoading(true);
    try {
      const { data } = await api.get(`/exam-library/questions/${encodeURIComponent(difficulty)}`, { params });
      setQuestions(data.filter(q => q.questionText !== '__placeholder__'));
    } catch (err) {
      setError('שגיאה בטעינת השאלות');
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  /* ─── לחיצה על רמת קושי ─── */
  const handleDiffClick = (diff) => {
    if (openDiff === diff) {
      setOpenDiff(null);
      setTree({});
      setOpenLevel2(null);
      setSelectedLeaf(null);
      setQuestions([]);
    } else {
      setOpenDiff(diff);
      setOpenLevel2(null);
      setSelectedLeaf(null);
      setQuestions([]);
      loadTree(diff);
    }
  };

  /* ─── לחיצה על חלק/מסכתא (רמה 2) ─── */
  const handleLevel2Click = (name) => {
    if (openLevel2 === name) {
      setOpenLevel2(null);
      setSelectedLeaf(null);
      setQuestions([]);
    } else {
      setOpenLevel2(name);
      setSelectedLeaf(null);
      setQuestions([]);
    }
  };

  /* ─── לחיצה על סימן/פרק (עלה) ─── */
  const handleLeafClick = (parentName, leafValue) => {
    const numVal = IS_GEMARA && typeof leafValue === 'object' ? leafValue.perek : leafValue;
    const perekNameVal = IS_GEMARA && typeof leafValue === 'object' ? leafValue.perekName : null;
    const leaf = IS_GEMARA
      ? { tractate: parentName, perek: numVal, perekName: perekNameVal }
      : { chelek: parentName, siman: numVal };

    if (
      selectedLeaf &&
      ((IS_GEMARA && selectedLeaf.tractate === parentName && selectedLeaf.perek === numVal) ||
       (!IS_GEMARA && selectedLeaf.chelek === parentName && selectedLeaf.siman === numVal))
    ) {
      setSelectedLeaf(null);
      setQuestions([]);
      return;
    }

    setSelectedLeaf(leaf);
    const params = IS_GEMARA
      ? { tractate: parentName, perek: numVal }
      : { chelek: parentName, siman: numVal };
    loadQuestions(openDiff, params);
  };

  /* ─── הורדת תבנית ─── */
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/exam-library/template', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'exam-library-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('שגיאה בהורדת התבנית');
    }
  };

  const handleDownloadDocxTemplate = async () => {
    try {
      const response = await api.get('/exam-library/template-docx', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'exam-library-template.docx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('שגיאה בהורדת תבנית ה-Word');
    }
  };

  const resetImportState = () => {
    setImportFile(null);
    setImportResult(null);
    setImportSourceTab(0);
    setDocxStep(0);
    setDocxPreview(null);
    setDocxQuestions([]);
    setDocxDifficulty('');
    setDocxChelek(HALACHA_PARTS[0]);
    setDocxTractate('');
    setDocxPerek('');
  };

  const closeImportDialog = () => {
    resetImportState();
    setImportDialog(false);
  };

  /* ─── ייבוא מ-Excel ─── */
  const handleImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const { data } = await api.post('/exam-library/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      setImportFile(null);
      loadDifficulties();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בייבוא');
    } finally {
      setImportLoading(false);
    }
  };

  /* ─── תצוגה מקדימה מוורד ─── */
  const handleDocxPreview = async () => {
    if (!importFile) return;
    if (!docxDifficulty.trim()) {
      setError('יש לבחור רמת קושי');
      return;
    }
    if (!IS_GEMARA && !docxChelek) {
      setError('יש לבחור חלק');
      return;
    }
    if (IS_GEMARA && !docxTractate.trim()) {
      setError('יש לבחור מסכתא');
      return;
    }
    setImportLoading(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const { data } = await api.post('/exam-library/import-docx/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocxPreview(data);
      setDocxQuestions((data.questions || []).map((q) => ({
        ...q,
        include: true,
        editSiman: q.siman,
        editPerek: q.perek || (docxPerek ? Number(docxPerek) : ''),
      })));
      setDocxStep(1);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בפיענוח קובץ הוורד');
    } finally {
      setImportLoading(false);
    }
  };

  /* ─── אישור ייבוא מוורד ─── */
  const handleDocxCommit = async () => {
    const selected = docxQuestions.filter((q) => q.include);
    if (!selected.length) {
      setError('לא נבחרו שאלות לייבוא');
      return;
    }
    setImportLoading(true);
    setImportResult(null);
    try {
      const payload = {
        difficulty: docxDifficulty.trim(),
        questions: selected.map((q) => ({
          include: true,
          questionText: q.questionText,
          siman: Number(q.editSiman || q.siman),
          perek: IS_GEMARA ? Number(q.editPerek || docxPerek) : undefined,
          tractate: IS_GEMARA ? docxTractate.trim() : undefined,
          starred: !!q.starred,
        })),
      };
      if (IS_GEMARA) {
        payload.tractate = docxTractate.trim();
        if (docxPerek) payload.perek = Number(docxPerek);
      } else {
        payload.chelek = docxChelek;
      }
      const { data } = await api.post('/exam-library/import-docx/commit', payload);
      setImportResult(data);
      setSuccess(`יובאו ${data.inserted} שאלות מוורד`);
      loadDifficulties();
      if (openDiff === docxDifficulty.trim()) {
        loadTree(openDiff);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בייבוא מוורד');
    } finally {
      setImportLoading(false);
    }
  };

  const includedDocxCount = docxQuestions.filter((q) => q.include).length;

  /* ─── יצירת רמת קושי ─── */
  const handleCreateDifficulty = async () => {
    if (!newDiffName.trim()) return;
    try {
      await api.post('/exam-library/difficulties', { name: newDiffName.trim() });
      setNewDiffDialog(false);
      setNewDiffName('');
      setSuccess('רמת קושי נוצרה בהצלחה');
      loadDifficulties();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה ביצירת רמת קושי');
    }
  };

  /* ─── מחיקת רמת קושי ─── */
  const handleDeleteDifficulty = async (diff) => {
    if (!(await ckConfirm({ title: 'מחיקת רמת קושי', message: `למחוק את רמת הקושי "${diff}" וכל השאלות שבה?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/exam-library/difficulties/${encodeURIComponent(diff)}`);
      setSuccess('רמת קושי נמחקה');
      if (openDiff === diff) {
        setOpenDiff(null);
        setTree({});
      }
      loadDifficulties();
    } catch (err) {
      setError('שגיאה במחיקה');
    }
  };

  /* ─── מחיקת תיקיה שלמה (מסכתא / חלק) כולל כל שאלותיה ─── */
  const handleDeleteFolder = async (parentName) => {
    const label = IS_GEMARA ? `מסכת "${parentName}"` : `"${parentName}"`;
    if (!(await ckConfirm({ title: 'מחיקת תיקייה', message: `למחוק את ${label} וכל הסימנים/פרקים והשאלות שבה?\nפעולה זו אינה הפיכה.`, confirmLabel: 'מחיקה' }))) return;
    try {
      const params = { difficulty: openDiff };
      if (IS_GEMARA) params.tractate = parentName;
      else params.chelek = parentName;
      await api.delete('/exam-library/folder', { params });
      if (selectedLeaf && (
        (IS_GEMARA && selectedLeaf.tractate === parentName) ||
        (!IS_GEMARA && selectedLeaf.chelek === parentName)
      )) {
        setSelectedLeaf(null);
        setQuestions([]);
      }
      if (openLevel2 === parentName) setOpenLevel2(null);
      setSuccess(IS_GEMARA ? 'מסכתא נמחקה' : 'חלק נמחק');
      loadTree(openDiff);
    } catch (err) {
      setError('שגיאה במחיקה');
    }
  };

  /* ─── מחיקת עלה (סימן/פרק) כולל כל שאלותיו ─── */
  const handleDeleteLeaf = async (parentName, val) => {
    const numVal = IS_GEMARA && typeof val === 'object' ? val.perek : val;
    const label = IS_GEMARA ? `פרק ${toHebrewNumeral(numVal)}` : `סימן ${toHebrewNumeral(numVal)}`;
    if (!(await ckConfirm({ title: 'מחיקה', message: `למחוק את ${label} מ"${parentName}"?\nכל השאלות שתחתיו יימחקו לצמיתות.`, confirmLabel: 'מחיקה' }))) return;
    try {
      const params = { difficulty: openDiff };
      if (IS_GEMARA) { params.tractate = parentName; params.perek = numVal; }
      else { params.chelek = parentName; params.siman = numVal; }
      await api.delete('/exam-library/leaf', { params });
      if (selectedLeaf && (
        (IS_GEMARA && selectedLeaf.tractate === parentName && selectedLeaf.perek === numVal) ||
        (!IS_GEMARA && selectedLeaf.chelek === parentName && selectedLeaf.siman === numVal)
      )) {
        setSelectedLeaf(null);
        setQuestions([]);
      }
      setSuccess(IS_GEMARA ? 'פרק נמחק' : 'סימן נמחק');
      loadTree(openDiff);
    } catch (err) {
      setError('שגיאה במחיקה');
    }
  };

  /* ─── הוספת רצף סימנים/פרקים ─── */
  const handleCreateLeaf = async () => {
    const from = Number(newLeafFrom);
    const to = Number(newLeafTo);
    if (!from || !to || from < 1 || to < from) {
      setError(IS_GEMARA ? 'טווח פרקים לא תקין' : 'טווח סימנים לא תקין');
      return;
    }
    try {
      const body = { difficulty: openDiff, from, to };
      if (IS_GEMARA) {
        body.tractate = newLeafParent;
      } else {
        body.chelek = newLeafParent;
      }
      await api.post('/exam-library/range', body);
      setNewLeafDialog(false);
      setNewLeafFrom('');
      setNewLeafTo('');
      setSuccess(IS_GEMARA ? 'פרקים נוספו בהצלחה' : 'סימנים נוספו בהצלחה');
      loadTree(openDiff);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בהוספה');
    }
  };

  /* ─── הוספה / עריכת שאלה ─── */
  const handleSaveQuestion = async () => {
    const text = sanitizeEditorHtml(editorRef.current?.innerHTML || '');
    if (!text) return;
    try {
      if (editingQuestion) {
        await api.put(`/exam-library/questions/${editingQuestion._id}`, {
          questionText: text,
          starred: questionStarred,
          version: editingQuestion.version,
        });
        setSuccess('שאלה עודכנה בהצלחה');
      } else {
        const body = {
          difficulty: openDiff,
          questionText: text,
          starred: questionStarred,
        };
        if (IS_GEMARA) {
          body.tractate = selectedLeaf.tractate;
          body.perek = selectedLeaf.perek;
        } else {
          body.chelek = selectedLeaf.chelek;
          body.siman = selectedLeaf.siman;
        }
        await api.post('/exam-library/questions', body);
        setSuccess('שאלה נוספה בהצלחה');
      }
      setQuestionDialog(false);
      setQuestionText('');
      setQuestionStarred(false);
      setEditingQuestion(null);
      // רענון שאלות
      const params = IS_GEMARA
        ? { tractate: selectedLeaf.tractate, perek: selectedLeaf.perek }
        : { chelek: selectedLeaf.chelek, siman: selectedLeaf.siman };
      loadQuestions(openDiff, params);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת שאלה');
    }
  };

  const handleToggleStar = async (q) => {
    try {
      const updated = await api.put(`/exam-library/questions/${q._id}`, {
        starred: !q.starred,
        version: q.version,
      });
      setQuestions((prev) => prev.map((row) => (
        row._id === q._id ? { ...row, starred: updated.data.starred, version: updated.data.version } : row
      )));
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בעדכון כוכבית');
    }
  };

  /* ─── מחיקת שאלה ─── */
  const handleDeleteQuestion = async (q) => {
    if (!(await ckConfirm({ title: 'מחיקת שאלה', message: 'למחוק שאלה זו?', confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/exam-library/questions/${q._id}`);
      setSuccess('שאלה נמחקה');
      const params = IS_GEMARA
        ? { tractate: selectedLeaf.tractate, perek: selectedLeaf.perek }
        : { chelek: selectedLeaf.chelek, siman: selectedLeaf.siman };
      loadQuestions(openDiff, params);
    } catch (err) {
      setError('שגיאה במחיקת שאלה');
    }
  };

  /* ─── פתיחת דיאלוג עריכה ─── */
  const openEditQuestion = (q) => {
    setEditingQuestion(q);
    setQuestionText(q.questionText);
    setQuestionStarred(!!q.starred);
    setQuestionDialog(true);
  };

  const openAddQuestion = () => {
    setEditingQuestion(null);
    setQuestionText('');
    setQuestionStarred(false);
    setQuestionDialog(true);
  };

  /* ─── פתיחת דיאלוג הוספת סימן/פרק ─── */
  const openNewLeaf = (parentName) => {
    if (IS_GEMARA) {
      // גמרא — הוספת פרק בודד עם שם
      setAddPerekParent(parentName);
      setAddPerekNum('');
      setAddPerekName('');
      setAddPerekDialog(true);
    } else {
      setNewLeafParent(parentName);
      setNewLeafFrom('');
      setNewLeafTo('');
      setNewLeafDialog(true);
    }
  };

  /* ─── הוספת פרק בודד (גמרא) ─── */
  const handleAddSinglePerek = async () => {
    const num = parseInt(addPerekNum, 10);
    if (!num || num < 1) { setError('מספר פרק חייב להיות 1 לפחות'); return; }
    try {
      await api.post('/exam-library/range', {
        difficulty: openDiff,
        tractate: addPerekParent,
        perakim: [{ perek: num, name: addPerekName.trim() || null }],
      });
      setAddPerekDialog(false);
      setSuccess('פרק נוסף בהצלחה');
      loadTree(openDiff);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בהוספת פרק');
    }
  };

  /* ─── הוספת מסכתא חדשה (גמרא) ─── */
  const handleCreateTractate = async () => {
    if (!newTractateValue?.trim()) return;

    if (newTractateMode === 'named') {
      // מצב פרקים עם שמות
      const validPerakim = newTractatePerakim.filter(p => p.perek || p.name?.trim());
      if (!validPerakim.length) { setError('יש להוסיף לפחות פרק אחד'); return; }
      for (const p of validPerakim) {
        if (!p.perek || parseInt(p.perek, 10) < 1) {
          setError('מספר פרק חייב להיות 1 לפחות');
          return;
        }
      }
      try {
        await api.post('/exam-library/range', {
          difficulty: openDiff,
          tractate: newTractateValue.trim(),
          perakim: validPerakim.map(p => ({ perek: parseInt(p.perek, 10), name: p.name?.trim() || null })),
        });
        setNewTractateDialog(false);
        setNewTractateValue('');
        setNewTractatePerakim([{ perek: '', name: '' }]);
        setNewTractateMode('range');
        setSuccess('מסכתא נוספה בהצלחה');
        loadTree(openDiff);
      } catch (err) {
        setError(err.response?.data?.error || 'שגיאה בהוספת מסכתא');
      }
    } else {
      // מצב רצף מספרים
      const fromVal = Number(newTractatePerekFrom);
      const toVal = Number(newTractatePerekTo);
      if (!fromVal || fromVal < 1) { setError('מספר פרק חייב להיות 1 לפחות'); return; }
      if (!toVal || toVal < fromVal) { setError('פרק סיום חייב להיות גדול או שווה לפרק התחלה'); return; }
      try {
        await api.post('/exam-library/range', {
          difficulty: openDiff,
          tractate: newTractateValue.trim(),
          from: fromVal,
          to: toVal,
        });
        setNewTractateDialog(false);
        setNewTractateValue('');
        setNewTractatePerekFrom('');
        setNewTractatePerekTo('');
        setNewTractateMode('range');
        setSuccess('מסכתא נוספה בהצלחה');
        loadTree(openDiff);
      } catch (err) {
        setError(err.response?.data?.error || 'שגיאה בהוספת מסכתא');
      }
    }
  };

  /* ─── helper: תווית עלה ─── */
  const leafLabel = (val) => {
    if (IS_GEMARA && typeof val === 'object') {
      const num = val.perek;
      const name = val.perekName;
      return name ? `פרק ${toHebrewNumeral(num)} — ${name}` : `פרק ${toHebrewNumeral(num)}`;
    }
    return IS_GEMARA ? `פרק ${toHebrewNumeral(val)}` : `סימן ${toHebrewNumeral(val)}`;
  };

  /* ─── חילוץ ערך מספרי מעלה ─── */
  const leafNumericValue = (val) => {
    if (IS_GEMARA && typeof val === 'object') return val.perek;
    return val;
  };

  /* ─── האם עלה נבחר ─── */
  const isLeafSelected = (parentName, val) => {
    if (!selectedLeaf) return false;
    const numVal = leafNumericValue(val);
    if (IS_GEMARA) return selectedLeaf.tractate === parentName && selectedLeaf.perek === numVal;
    return selectedLeaf.chelek === parentName && selectedLeaf.siman === numVal;
  };

  return (
    <Box sx={inNextDesign ? { maxWidth: 1200, width: '100%' } : { p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* כותרת כללית */}
      <AdminPageHeader
        title={MODE_LABEL}
        sub="שאלות, טפסי בסיס ודפים נוספים — והנפקת בחינות להדפסה"
        primary={{ label: 'הנפקת בחינות', icon: 'file', onClick: () => setIssuanceOpen(true) }}
        legacy={(
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <MenuBookOutlinedIcon sx={{ fontSize: 32, color: 'var(--ck-primary, #1565c0)' }} />
                <Typography variant="h5" fontWeight={700} color="var(--ck-primary, #1a237e)">
                  {MODE_LABEL}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={() => setIssuanceOpen(true)}
                sx={{
                  bgcolor: 'var(--ck-danger, #c62828)',
                  '&:hover': { bgcolor: 'var(--ck-danger, #b71c1c)' },
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                הנפקת בחינות
              </Button>
            </Box>
        )}
      />

      {/* כרטיסיות */}
      <Paper sx={{ borderRadius: 3, mb: 2.5, overflow: 'hidden', boxShadow: 'var(--ck-shadow-1, 0 2px 8px rgba(0,0,0,0.06))' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            bgcolor: 'var(--ck-surface-2, #fafafa)',
            '& .MuiTab-root': {
              fontWeight: 700,
              fontSize: 14,
              py: 1.5,
            },
            '& .Mui-selected': { color: 'var(--ck-primary, #1565c0)' },
            '& .MuiTabs-indicator': { bgcolor: 'var(--ck-primary, #1565c0)', height: 3 },
          }}
        >
          <Tab
            icon={<QuizOutlinedIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="שאלות"
          />
          <Tab
            icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="אלמנטים עיצוביים"
          />
        </Tabs>
      </Paper>

      {/* תוכן כרטיסייה 0 — שאלות (הקוד המקורי) */}
      {activeTab === 0 && (
      <>
      {/* כותרת */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ borderRadius: 2, borderColor: 'var(--ck-primary, #1565c0)', color: 'var(--ck-primary, #1565c0)' }}
          >
            הורד תבנית
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => {
              resetImportState();
              setImportDialog(true);
            }}
            sx={{ borderRadius: 2, borderColor: 'var(--ck-success, #43a047)', color: 'var(--ck-success, #43a047)' }}
          >
            ייבוא
          </Button>
          <Button
            variant="contained"
            startIcon={<CreateNewFolderIcon />}
            onClick={() => setNewDiffDialog(true)}
            sx={{
              bgcolor: 'var(--ck-primary, #1565c0)',
              '&:hover': { bgcolor: 'var(--ck-primary-hover, #0d47a1)' },
              borderRadius: 2,
            }}
          >
            רמת קושי חדשה
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : difficulties.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <CreateNewFolderIcon sx={{ fontSize: 64, color: 'var(--ck-text-3, #bdbdbd)', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            אין עדיין רמות קושי
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            לחץ על "רמת קושי חדשה" כדי להתחיל לבנות את הספרייה
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: 'clip', boxShadow: 'var(--ck-shadow-1, 0 2px 12px rgba(0,0,0,0.08))' }}>
          <List disablePadding>
            {difficulties.map((diff, idx) => (
              <React.Fragment key={diff}>
                {idx > 0 && <Divider />}
                {/* ═══ רמת קושי ═══ */}
                <ListItemButton
                  onClick={() => handleDiffClick(diff)}
                  sx={{
                    py: 1.8, px: 3,
                    bgcolor: openDiff === diff ? 'var(--ck-primary-soft, #e3f2fd)' : 'transparent',
                    '&:hover': { bgcolor: openDiff === diff ? 'var(--ck-primary-soft, #bbdefb)' : 'var(--ck-surface-2, #f5f5f5)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {openDiff === diff
                      ? <FolderOpenIcon sx={{ color: 'var(--ck-primary, #1565c0)' }} />
                      : <FolderIcon sx={{ color: 'var(--ck-warning, #ffa000)' }} />}
                  </ListItemIcon>
                  <ListItemText
                    primary={diff}
                    primaryTypographyProps={{ fontWeight: 700, fontSize: 16, color: 'var(--ck-primary, #1a237e)' }}
                  />
                  <Tooltip title="מחק רמת קושי">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeleteDifficulty(diff); }}
                      sx={{ ml: 1, color: 'var(--ck-danger, #ef5350)' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {openDiff === diff ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                {/* ═══ רמה 2: חלקים/מסכתות ═══ */}
                <Collapse in={openDiff === diff} timeout="auto" unmountOnExit>
                  {treeLoading ? (
                    <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
                  ) : Object.keys(tree).length === 0 ? (
                    <Box sx={{ py: 3, px: 4, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {IS_GEMARA ? 'אין מסכתות עדיין' : 'אין תוכן עדיין'}
                      </Typography>
                      {IS_GEMARA && (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setNewTractateDialog(true)}
                          sx={{ color: 'var(--ck-success, #43a047)' }}
                        >
                          הוסף מסכתא
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <List disablePadding sx={{ bgcolor: 'var(--ck-surface-2, #fafafa)' }}>
                      {(IS_GEMARA
                        ? Object.entries(tree)
                        : Object.entries(tree).sort((a, b) => HALACHA_PARTS.indexOf(a[0]) - HALACHA_PARTS.indexOf(b[0]))
                      ).map(([parentName, leaves]) => (
                        <React.Fragment key={parentName}>
                          {/* חלק/מסכתא */}
                          <ListItemButton
                            onClick={() => handleLevel2Click(parentName)}
                            sx={{
                              pl: 6, py: 1.2,
                              position: 'sticky',
                              top: 64,
                              zIndex: 1,
                              bgcolor: 'var(--ck-surface-2, #fafafa)',
                              '&:hover': { bgcolor: 'var(--ck-surface-3, #f0f0f0)' },
                              borderBottom: openLevel2 === parentName ? '1px solid var(--ck-border, #e0e0e0)' : 'none',
                              boxShadow: openLevel2 === parentName ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {openLevel2 === parentName
                                ? <FolderOpenIcon fontSize="small" sx={{ color: 'var(--ck-success, #43a047)' }} />
                                : <FolderIcon fontSize="small" sx={{ color: 'var(--ck-success, #66bb6a)' }} />}
                            </ListItemIcon>
                            <ListItemText
                              primary={parentName}
                              primaryTypographyProps={{ fontWeight: 600, fontSize: 15, color: 'var(--ck-success, #2e7d32)' }}
                            />
                            <Chip
                              label={`${leaves.length} ${IS_GEMARA ? 'פרקים' : 'סימנים'}`}
                              size="small"
                              sx={{ bgcolor: 'var(--ck-success-soft, #e8f5e9)', color: 'var(--ck-success, #2e7d32)', fontWeight: 600, mr: 1 }}
                            />
                            <Tooltip title={IS_GEMARA ? 'הוסף פרק' : 'הוסף סימן'}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); openNewLeaf(parentName); }}
                                sx={{ color: 'var(--ck-success, #43a047)' }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={IS_GEMARA ? 'מחק מסכתא' : 'מחק חלק'}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleDeleteFolder(parentName); }}
                                sx={{ color: 'var(--ck-danger, #ef5350)' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {openLevel2 === parentName ? <ExpandLess /> : <ExpandMore />}
                          </ListItemButton>

                          {/* ═══ רמה 3: סימנים/פרקים ═══ */}
                          <Collapse in={openLevel2 === parentName} timeout="auto" unmountOnExit>
                            <List disablePadding sx={{ bgcolor: 'var(--ck-surface-2, #f5f5f5)' }}>
                              {leaves.map((val) => (
                                <ListItemButton
                                  key={leafNumericValue(val)}
                                  onClick={() => handleLeafClick(parentName, val)}
                                  selected={isLeafSelected(parentName, val)}
                                  sx={{
                                    pl: 10, py: 0.8,
                                    '& .leaf-delete': { opacity: 0, transition: 'opacity 0.15s' },
                                    '&:hover .leaf-delete': { opacity: 1 },
                                    '&.Mui-selected': {
                                      bgcolor: 'var(--ck-primary-soft, #e8eaf6)',
                                      borderRight: '3px solid var(--ck-primary, #3f51b5)',
                                      '&:hover': { bgcolor: 'var(--ck-primary-soft, #c5cae9)' },
                                    },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <DescriptionOutlinedIcon
                                      fontSize="small"
                                      sx={{ color: isLeafSelected(parentName, val) ? 'var(--ck-primary, #3f51b5)' : 'var(--ck-text-3, #9e9e9e)' }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={leafLabel(val)}
                                    primaryTypographyProps={{
                                      fontSize: 14,
                                      fontWeight: isLeafSelected(parentName, val) ? 700 : 400,
                                      color: isLeafSelected(parentName, val) ? 'var(--ck-primary, #1a237e)' : 'var(--ck-text, #424242)',
                                    }}
                                  />
                                  <Tooltip title="מחק">
                                    <IconButton
                                      size="small"
                                      className="leaf-delete"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteLeaf(parentName, val); }}
                                      sx={{ color: 'var(--ck-danger, #ef5350)' }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </ListItemButton>
                              ))}
                            </List>
                          </Collapse>
                        </React.Fragment>
                      ))}
                      {IS_GEMARA && (
                        <Box sx={{ py: 1.5, px: 6 }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setNewTractateDialog(true)}
                            sx={{ color: 'var(--ck-success, #43a047)' }}
                          >
                            הוסף מסכתא
                          </Button>
                        </Box>
                      )}
                    </List>
                  )}
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* ═══════════ דיאלוג שאלות לסימן/פרק ═══════════ */}
      <Dialog
        open={!!selectedLeaf}
        onClose={() => { setSelectedLeaf(null); setQuestions([]); }}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3, minHeight: 400, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuizOutlinedIcon sx={{ color: 'var(--ck-primary, #3f51b5)' }} />
            <Typography variant="h6" fontWeight={700} color="var(--ck-primary, #1a237e)" component="span">
              שאלות —{' '}
              {selectedLeaf && (IS_GEMARA
                ? `${selectedLeaf.tractate} פרק ${toHebrewNumeral(selectedLeaf.perek)}${selectedLeaf.perekName ? ` — ${selectedLeaf.perekName}` : ''}`
                : `${selectedLeaf.chelek} סימן ${toHebrewNumeral(selectedLeaf.siman)}`)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openAddQuestion}
              sx={{
                bgcolor: 'var(--ck-primary, #3f51b5)',
                '&:hover': { bgcolor: 'var(--ck-primary-hover, #303f9f)' },
                borderRadius: 2,
              }}
            >
              הוסף שאלה
            </Button>
            <IconButton onClick={() => { setSelectedLeaf(null); setQuestions([]); }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {questionsLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : questions.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <QuizOutlinedIcon sx={{ fontSize: 48, color: 'var(--ck-text-3, #bdbdbd)', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                אין שאלות עדיין
              </Typography>
              <Typography variant="body2" color="text.secondary">
                לחץ על "הוסף שאלה" כדי להתחיל
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {questions.map((q, idx) => (
                <Paper
                  key={q._id}
                  variant="outlined"
                  sx={{
                    p: 2, borderRadius: 2,
                    display: 'flex', alignItems: 'flex-start', gap: 2,
                    borderColor: 'var(--ck-border, #e0e0e0)',
                    '&:hover': { borderColor: 'var(--ck-primary-line, #90caf9)', bgcolor: 'var(--ck-surface-2, #fafafa)' },
                    transition: 'all 0.15s',
                  }}
                >
                  <Chip
                    label={idx + 1}
                    size="small"
                    sx={{
                      bgcolor: 'var(--ck-primary-soft, #e8eaf6)', color: 'var(--ck-primary, #3f51b5)',
                      fontWeight: 700, minWidth: 32, mt: 0.3,
                    }}
                  />
                  {q.starred && (
                    <Typography component="span" sx={{ color: 'var(--ck-danger, #c62828)', fontWeight: 700, mt: 0.5 }}>*</Typography>
                  )}
                  <Typography
                    component="div"
                    sx={{
                      flex: 1, fontSize: 15, lineHeight: 1.7,
                      color: 'var(--ck-text, #212121)',
                      '& b': { fontWeight: 700 },
                      '& i': { fontStyle: 'italic' },
                    }}
                    dangerouslySetInnerHTML={{ __html: safeRenderQuestion(q.questionText) }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title={q.starred ? 'הסר כוכבית' : 'סמן בכוכבית'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStar(q)}
                        sx={{ color: q.starred ? 'var(--ck-warning, #f9a825)' : 'var(--ck-text-3, #bdbdbd)' }}
                      >
                        {q.starred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="ערוך">
                      <IconButton size="small" onClick={() => openEditQuestion(q)} sx={{ color: 'var(--ck-primary, #1976d2)' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="מחק">
                      <IconButton size="small" onClick={() => handleDeleteQuestion(q)} sx={{ color: 'var(--ck-danger, #ef5350)' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════ דיאלוג רמת קושי חדשה ═══════════ */}
      <Dialog
        open={newDiffDialog}
        onClose={() => setNewDiffDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>רמת קושי חדשה</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="שם רמת הקושי"
            placeholder='לדוג׳: "קל", "בינוני", "קשה"'
            value={newDiffName}
            onChange={(e) => setNewDiffName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateDifficulty()}
            sx={{ mt: 1 }}
          />
          {!IS_GEMARA && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              ייווצרו אוטומטית 4 תיקיות: אורח חיים, יורה דעה, אבן העזר, חושן משפט
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewDiffDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleCreateDifficulty} startIcon={<SaveIcon />}>
            צור
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════ דיאלוג הוספת סימן/פרק (רצף) ═══════════ */}
      <Dialog
        open={newLeafDialog}
        onClose={() => setNewLeafDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {IS_GEMARA ? `הוספת פרקים ל${newLeafParent}` : `הוספת סימנים ל${newLeafParent}`}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              autoFocus
              fullWidth
              type="number"
              label={IS_GEMARA ? 'מפרק' : 'מסימן'}
              value={newLeafFrom}
              onChange={(e) => setNewLeafFrom(e.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              fullWidth
              type="number"
              label={IS_GEMARA ? 'עד פרק' : 'עד סימן'}
              value={newLeafTo}
              onChange={(e) => setNewLeafTo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateLeaf()}
              inputProps={{ min: 1 }}
            />
          </Box>
          {newLeafFrom && newLeafTo && Number(newLeafTo) >= Number(newLeafFrom) && (
            <Typography variant="caption" color="text.secondary">
              ייצרו {Number(newLeafTo) - Number(newLeafFrom) + 1} {IS_GEMARA ? 'פרקים' : 'סימנים'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewLeafDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleCreateLeaf} startIcon={<AddIcon />}>
            הוסף
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════ דיאלוג הוספת מסכתא (גמרא) ═══════════ */}
      {IS_GEMARA && (
        <Dialog
          open={newTractateDialog}
          onClose={() => setNewTractateDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>הוספת מסכתא חדשה</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              freeSolo
              options={TRACTATE_LIST}
              value={newTractateValue}
              onInputChange={(_e, v) => setNewTractateValue(v)}
              renderInput={(params) => (
                <TextField {...params} label="שם מסכתא" sx={{ mt: 1 }} autoFocus />
              )}
            />

            {/* בחירת מצב הוספה */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={newTractateMode === 'range' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setNewTractateMode('range')}
                sx={{ borderRadius: 2, flex: 1 }}
              >
                טווח מספרים
              </Button>
              <Button
                variant={newTractateMode === 'named' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setNewTractateMode('named')}
                sx={{ borderRadius: 2, flex: 1 }}
              >
                פרקים עם שמות
              </Button>
            </Box>

            {newTractateMode === 'range' ? (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="מפרק"
                    value={newTractatePerekFrom}
                    onChange={(e) => setNewTractatePerekFrom(e.target.value)}
                    inputProps={{ min: 1 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="עד פרק"
                    value={newTractatePerekTo}
                    onChange={(e) => setNewTractatePerekTo(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateTractate()}
                    inputProps={{ min: 1 }}
                  />
                </Box>
                {newTractatePerekFrom && newTractatePerekTo && Number(newTractatePerekTo) >= Number(newTractatePerekFrom) && (
                  <Typography variant="caption" color="text.secondary">
                    ייצרו {Number(newTractatePerekTo) - Number(newTractatePerekFrom) + 1} פרקים
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  הוסף פרקים עם שמות — ניתן להוסיף לא בסדר עולה, הסדר יישמר אוטומטית.
                </Typography>
                {newTractatePerakim.map((p, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      type="number"
                      label="מספר"
                      value={p.perek}
                      onChange={(e) => {
                        const arr = [...newTractatePerakim];
                        arr[idx] = { ...arr[idx], perek: e.target.value };
                        setNewTractatePerakim(arr);
                      }}
                      inputProps={{ min: 1 }}
                      sx={{ width: 100 }}
                      size="small"
                    />
                    <TextField
                      label="שם פרק"
                      value={p.name}
                      onChange={(e) => {
                        const arr = [...newTractatePerakim];
                        arr[idx] = { ...arr[idx], name: e.target.value };
                        setNewTractatePerakim(arr);
                      }}
                      sx={{ flex: 1 }}
                      size="small"
                      placeholder='לדוג׳: "מאימתי"'
                    />
                    {newTractatePerakim.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => setNewTractatePerakim(arr => arr.filter((_, i) => i !== idx))}
                        sx={{ color: 'var(--ck-danger, #ef5350)' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setNewTractatePerakim(arr => [...arr, { perek: '', name: '' }])}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  פרק נוסף
                </Button>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setNewTractateDialog(false)}>ביטול</Button>
            <Button variant="contained" onClick={handleCreateTractate} startIcon={<AddIcon />}>
              הוסף
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ═══════════ דיאלוג הוספת פרק בודד (גמרא) ═══════════ */}
      {IS_GEMARA && (
        <Dialog
          open={addPerekDialog}
          onClose={() => setAddPerekDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>הוספת פרק ל{addPerekParent}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
            <TextField
              autoFocus
              type="number"
              label="מספר פרק"
              value={addPerekNum}
              onChange={(e) => setAddPerekNum(e.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="שם פרק (אופציונלי)"
              value={addPerekName}
              onChange={(e) => setAddPerekName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSinglePerek()}
              placeholder='לדוג׳: "מאימתי"'
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAddPerekDialog(false)}>ביטול</Button>
            <Button variant="contained" onClick={handleAddSinglePerek} startIcon={<AddIcon />}>
              הוסף
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ═══════════ דיאלוג שאלה ═══════════ */}
      <Dialog
        open={questionDialog}
        onClose={() => { setQuestionDialog(false); setEditingQuestion(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingQuestion ? 'עריכת שאלה' : 'הוספת שאלה'}
        </DialogTitle>
        <DialogContent>
          {/* תיבת עריכה עשירה עם סרגל כלים להדגשה/נטייה */}
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: 'flex', gap: 0.5, alignItems: 'center',
                border: '1px solid var(--ck-border-strong, rgba(0,0,0,0.23))',
                borderBottom: 'none',
                borderRadius: '4px 4px 0 0',
                bgcolor: 'var(--ck-surface-2, #f5f5f5)',
                px: 0.75, py: 0.5,
              }}
            >
              <Tooltip title="הדגשה (Ctrl+B)">
                <IconButton
                  size="small"
                  onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); document.execCommand('bold', false, null); }}
                  sx={{ color: 'var(--ck-text, #424242)', fontWeight: 900 }}
                >
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="נטוי (Ctrl+I)">
                <IconButton
                  size="small"
                  onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); document.execCommand('italic', false, null); }}
                  sx={{ color: 'var(--ck-text, #424242)' }}
                >
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="קו תחתי (Ctrl+U)">
                <IconButton
                  size="small"
                  onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); document.execCommand('underline', false, null); }}
                  sx={{ color: 'var(--ck-text, #424242)' }}
                >
                  <FormatUnderlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pr: 1 }}>
                סמן טקסט ולחץ B / I / U לעיצוב
              </Typography>
            </Box>
            <Box
              ref={editorRefCallback}
              contentEditable
              suppressContentEditableWarning
              dir="rtl"
              sx={{
                minHeight: 90,
                border: '1px solid var(--ck-border-strong, rgba(0,0,0,0.23))',
                borderRadius: '0 0 4px 4px',
                p: 1.5,
                fontSize: 15,
                lineHeight: 1.8,
                fontFamily: 'inherit',
                outline: 'none',
                whiteSpace: 'pre-wrap',
                '&:focus': {
                  borderColor: 'var(--ck-primary, #1976d2)',
                  boxShadow: '0 0 0 2px rgba(25,118,210,0.15)',
                },
                '& b': { fontWeight: 700 },
                '& i': { fontStyle: 'italic' },
              }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={questionStarred}
                onChange={(e) => setQuestionStarred(e.target.checked)}
                icon={<StarBorderIcon />}
                checkedIcon={<StarIcon sx={{ color: 'var(--ck-warning, #f9a825)' }} />}
              />
            }
            label="סמן בכוכבית (למי שעושה כמה מבחנים)"
            sx={{ mt: 1.5 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setQuestionDialog(false); setEditingQuestion(null); setQuestionStarred(false); }}>
            ביטול
          </Button>
          <Button variant="contained" onClick={handleSaveQuestion} startIcon={<SaveIcon />}>
            {editingQuestion ? 'עדכן' : 'הוסף'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════ דיאלוג ייבוא Excel / Word ═══════════ */}
      <Dialog
        open={importDialog}
        onClose={() => { if (!importLoading) closeImportDialog(); }}
        maxWidth={importSourceTab === 1 && docxStep === 1 ? 'md' : 'sm'}
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <UploadFileIcon sx={{ color: 'var(--ck-success, #43a047)' }} />
          ייבוא שאלות
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Tabs
            value={importSourceTab}
            onChange={(_, v) => {
              if (importLoading) return;
              setImportSourceTab(v);
              setImportFile(null);
              setImportResult(null);
              setDocxStep(0);
              setDocxPreview(null);
              setDocxQuestions([]);
            }}
            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
          >
            <Tab label="Excel" sx={{ minHeight: 40 }} />
            <Tab label="Word" sx={{ minHeight: 40 }} />
          </Tabs>

          {/* ── Excel ── */}
          {importSourceTab === 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                העלה קובץ Excel עם כותרות:
                {IS_GEMARA
                  ? ' רמת_קושי | מסכתא | שם_פרק | שאלה'
                  : ' רמת_קושי | חלק | סימן | שאלה'}
              </Typography>
              {IS_GEMARA && (
                <Typography variant="caption" color="text.secondary">
                  מספרי הפרקים יוקצו אוטומטית לפי סדר ההופעה. אם שם פרק כבר קיים במסכתא, השאלה תתווסף לפרק הקיים.
                </Typography>
              )}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                size="small"
                sx={{ alignSelf: 'flex-start', color: 'var(--ck-primary, #1565c0)', borderColor: 'var(--ck-primary, #1565c0)' }}
              >
                הורד תבנית לדוגמה
              </Button>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  borderColor: importFile ? 'var(--ck-success, #43a047)' : 'var(--ck-border-strong, #bdbdbd)',
                  color: importFile ? 'var(--ck-success, #2e7d32)' : 'text.secondary',
                  bgcolor: importFile ? 'var(--ck-success-soft, #f1f8e9)' : 'transparent',
                }}
              >
                {importFile ? importFile.name : 'בחר קובץ xlsx...'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={(e) => { setImportFile(e.target.files[0] || null); setImportResult(null); }}
                />
              </Button>
            </>
          )}

          {/* ── Word: שלב הגדרות ── */}
          {importSourceTab === 1 && docxStep === 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {`העלה קובץ Word (.docx). המערכת מזהה כותרות «סימן …» או «סי' …» ופסקאות שאלה, ומשמרת הדגשה וקו תחתי.
                שורות תשובה (קווים תחתיים) ידולגו אוטומטית.`}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadDocxTemplate}
                size="small"
                sx={{ alignSelf: 'flex-start', color: 'var(--ck-primary, #1565c0)', borderColor: 'var(--ck-primary, #1565c0)' }}
              >
                הורד תבנית לדוגמה
              </Button>
              <FormControl fullWidth size="small">
                <InputLabel>רמת קושי</InputLabel>
                <Select
                  label="רמת קושי"
                  value={docxDifficulty}
                  onChange={(e) => setDocxDifficulty(e.target.value)}
                >
                  {difficulties.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!IS_GEMARA ? (
                <FormControl fullWidth size="small">
                  <InputLabel>חלק</InputLabel>
                  <Select
                    label="חלק"
                    value={docxChelek}
                    onChange={(e) => setDocxChelek(e.target.value)}
                  >
                    {HALACHA_PARTS.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <>
                  <Autocomplete
                    freeSolo
                    options={TRACTATE_LIST}
                    value={docxTractate}
                    onInputChange={(_, v) => setDocxTractate(v || '')}
                    renderInput={(params) => <TextField {...params} label="מסכתא" size="small" />}
                  />
                  <TextField
                    label="פרק (אופציונלי — ברירת מחדל לכל השאלות)"
                    size="small"
                    type="number"
                    value={docxPerek}
                    onChange={(e) => setDocxPerek(e.target.value)}
                    inputProps={{ min: 1 }}
                  />
                </>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  borderColor: importFile ? 'var(--ck-success, #43a047)' : 'var(--ck-border-strong, #bdbdbd)',
                  color: importFile ? 'var(--ck-success, #2e7d32)' : 'text.secondary',
                  bgcolor: importFile ? 'var(--ck-success-soft, #f1f8e9)' : 'transparent',
                }}
              >
                {importFile ? importFile.name : 'בחר קובץ docx...'}
                <input
                  type="file"
                  hidden
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => { setImportFile(e.target.files[0] || null); setImportResult(null); }}
                />
              </Button>
            </>
          )}

          {/* ── Word: תצוגה מקדימה ── */}
          {importSourceTab === 1 && docxStep === 1 && (
            <>
              {docxPreview?.stats && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  נמצאו {docxPreview.stats.questions} שאלות ב־{docxPreview.stats.uniqueSimanim || docxPreview.stats.simanim} סימנים
                  {' · '}
                  נבחרו {includedDocxCount} לייבוא
                  {' · '}
                  רמת קושי: {docxDifficulty}
                  {!IS_GEMARA && ` · חלק: ${docxChelek}`}
                  {IS_GEMARA && ` · מסכתא: ${docxTractate}`}
                </Alert>
              )}
              {docxPreview?.warnings?.length > 0 && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} display="block">
                    אזהרות ({docxPreview.warnings.length}):
                  </Typography>
                  <Box sx={{ maxHeight: 72, overflowY: 'auto' }}>
                    {docxPreview.warnings.map((w, i) => (
                      <Typography key={i} variant="caption" display="block">{w}</Typography>
                    ))}
                  </Box>
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  onClick={() => setDocxQuestions((qs) => qs.map((q) => ({ ...q, include: true })))}
                >
                  בחר הכל
                </Button>
                <Button
                  size="small"
                  onClick={() => setDocxQuestions((qs) => qs.map((q) => ({ ...q, include: false })))}
                >
                  נקה בחירה
                </Button>
              </Box>
              <Box sx={{ maxHeight: 420, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                {docxQuestions.map((q, idx) => (
                  <Box
                    key={`${q.sourceParagraphIndex}-${idx}`}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'flex-start',
                      px: 1.5,
                      py: 1.25,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      opacity: q.include ? 1 : 0.45,
                      bgcolor: q.include ? 'transparent' : 'action.hover',
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={!!q.include}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setDocxQuestions((qs) => qs.map((row, i) => (i === idx ? { ...row, include: checked } : row)));
                      }}
                      sx={{ mt: 0.25 }}
                    />
                    <Box sx={{ minWidth: 88 }}>
                      {!IS_GEMARA ? (
                        <TextField
                          size="small"
                          label="סימן"
                          type="number"
                          value={q.editSiman ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setDocxQuestions((qs) => qs.map((row, i) => (
                              i === idx ? { ...row, editSiman: v === '' ? '' : Number(v) } : row
                            )));
                          }}
                          inputProps={{ min: 1 }}
                          sx={{ width: 88 }}
                        />
                      ) : (
                        <TextField
                          size="small"
                          label="פרק"
                          type="number"
                          value={q.editPerek ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setDocxQuestions((qs) => qs.map((row, i) => (
                              i === idx ? { ...row, editPerek: v === '' ? '' : Number(v) } : row
                            )));
                          }}
                          inputProps={{ min: 1 }}
                          sx={{ width: 88 }}
                        />
                      )}
                      {q.simanLabel && !IS_GEMARA && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {q.simanLabel}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        fontSize: '0.9rem',
                        lineHeight: 1.55,
                        '& b': { fontWeight: 700 },
                        '& i': { fontStyle: 'italic' },
                        '& u': { textDecoration: 'underline' },
                      }}
                    >
                      {q.starred && (
                        <Typography
                          component="span"
                          sx={{ color: 'var(--ck-danger, #c62828)', fontWeight: 700, mr: 0.5 }}
                        >
                          *
                        </Typography>
                      )}
                      <Box
                        component="span"
                        dangerouslySetInnerHTML={{ __html: safeRenderQuestion(q.questionText) }}
                      />
                    </Box>
                    <Tooltip title={q.starred ? 'הסר כוכבית' : 'סמן בכוכבית'}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDocxQuestions((qs) => qs.map((row, i) => (
                            i === idx ? { ...row, starred: !row.starred } : row
                          )));
                        }}
                        sx={{ color: q.starred ? 'var(--ck-warning, #f9a825)' : 'var(--ck-text-3, #bdbdbd)', mt: 0.25 }}
                      >
                        {q.starred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
                {docxQuestions.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    לא זוהו שאלות בקובץ
                  </Typography>
                )}
              </Box>
            </>
          )}

          {importResult && (
            <Alert severity={importResult.errors?.length ? 'warning' : 'success'} sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                יובאו {importResult.inserted} שאלות מתוך {importResult.total}
              </Typography>
              {importResult.errors?.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 120, overflowY: 'auto' }}>
                  {importResult.errors.map((e, i) => (
                    <Typography key={i} variant="caption" display="block" color="warning.dark">{e}</Typography>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeImportDialog} disabled={importLoading}>
            סגור
          </Button>
          {importSourceTab === 0 && (
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!importFile || importLoading}
              startIcon={importLoading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
              sx={{ bgcolor: 'var(--ck-success, #43a047)', '&:hover': { bgcolor: 'var(--ck-success, #2e7d32)' } }}
            >
              {importLoading ? 'מייבא...' : 'ייבא'}
            </Button>
          )}
          {importSourceTab === 1 && docxStep === 0 && (
            <Button
              variant="contained"
              onClick={handleDocxPreview}
              disabled={!importFile || importLoading || !docxDifficulty}
              startIcon={importLoading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
              sx={{ bgcolor: 'var(--ck-success, #43a047)', '&:hover': { bgcolor: 'var(--ck-success, #2e7d32)' } }}
            >
              {importLoading ? 'מפענח...' : 'המשך לתצוגה מקדימה'}
            </Button>
          )}
          {importSourceTab === 1 && docxStep === 1 && !importResult && (
            <>
              <Button
                disabled={importLoading}
                onClick={() => {
                  setDocxStep(0);
                  setDocxPreview(null);
                  setDocxQuestions([]);
                }}
              >
                חזרה
              </Button>
              <Button
                variant="contained"
                onClick={handleDocxCommit}
                disabled={importLoading || includedDocxCount === 0}
                startIcon={importLoading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                sx={{ bgcolor: 'var(--ck-success, #43a047)', '&:hover': { bgcolor: 'var(--ck-success, #2e7d32)' } }}
              >
                {importLoading ? 'מייבא...' : `ייבא ${includedDocxCount} שאלות`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══════════ Snackbars ═══════════ */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      </>
      )}

      {/* תוכן כרטיסייה 1 — אלמנטים עיצוביים */}
      {activeTab === 1 && <ExamBlanks />}

      {/* אשף הנפקת בחינות */}
      <ExamIssuanceWizard
        open={issuanceOpen}
        onClose={() => setIssuanceOpen(false)}
      />
    </Box>
  );
};

export default ExamLibrary;
